import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getXeroBillQueue,
  getXeroBillsSummary,
  unmatchXeroBill,
} from "../services/xeroBillsService";
import XeroReconciliation from "./XeroReconciliation";

/**
 * New bills from Xero — P2P v1.
 *
 * Supplier bills that Dext has captured into Xero but that have not been recorded in
 * MyGlobe yet. Opening one pre-fills the expenditure form, so the work becomes confirming
 * the property and job rather than typing the whole invoice.
 *
 * Nothing on this screen creates a record. That happens when the person saves the
 * expenditure form as they do today.
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const fmtMoney = (v, currency = "GBP") =>
  v === null || v === undefined || v === ""
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP" }).format(
        Number(v)
      );

const PAGE_SIZE = 25;

const XeroBillsQueue = () => {
  const navigate = useNavigate();

  // Tab lives in the URL so the comparison can be linked to and survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "comparison" ? "comparison" : "queue";
  const setTab = (next) =>
    setSearchParams(next === "comparison" ? { tab: "comparison" } : {}, { replace: true });

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Committed filters — `search` is what has actually been submitted, so typing does not
  // fire a request per keystroke against a table of several thousand rows.
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("date");
  const [dir, setDir] = useState("desc");

  // Defaults to contractor suppliers: the client's process runs from a job, so those are the
  // bills where the Job selector is relevant. "all" adds utilities, rates and the rest.
  const [scope, setScope] = useState("contractors");
  const [scopeCounts, setScopeCounts] = useState({ contractors: 0, all: 0 });

  // Matching is a filter, not a gate. A bill we think is already recorded moves to the
  // second list — it does not disappear. If the match is wrong the bill is somewhere
  // findable rather than nowhere, which is the only way a bill can actually be lost.
  const [recorded, setRecorded] = useState("0");
  const [recordedCounts, setRecordedCounts] = useState({
    not_recorded: 0,
    recorded: 0,
    all: 0,
  });

  const load = useCallback(async () => {
    // The comparison tab fetches its own data; no point loading the queue behind it.
    if (tab !== "queue") return;
    setLoading(true);
    try {
      const [queueRes, summaryRes] = await Promise.all([
        getXeroBillQueue({ page, limit: PAGE_SIZE, search, sort, dir, scope, recorded }),
        getXeroBillsSummary(),
      ]);

      if (queueRes.success) {
        setRows(queueRes.data || []);
        setPagination(queueRes.pagination || { page: 1, pages: 1, total: 0 });
        if (queueRes.scope_counts) setScopeCounts(queueRes.scope_counts);
        if (queueRes.recorded_counts) setRecordedCounts(queueRes.recorded_counts);
      } else {
        toast.error(queueRes.message || "Failed to load bills from Xero");
      }

      if (summaryRes.success) setSummary(summaryRes.data);
    } catch (e) {
      toast.error(e.message || "Failed to load bills from Xero");
    } finally {
      setLoading(false);
    }
  }, [page, search, sort, dir, scope, recorded, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleSort = (col) => {
    if (sort === col) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(col);
      setDir("desc");
    }
    setPage(1);
  };

  const sortIndicator = (col) => (sort === col ? (dir === "asc" ? " ▲" : " ▼") : "");

  // Opens the dedicated single-bill screen, which fetches the prefill and suggestions.
  const record = (row) => navigate(`/xero-bills/${encodeURIComponent(row.xero_invoice_id)}`);

  // Rejecting a match sends the bill back to the work list. This is what makes it safe for
  // matching to guess at all — a wrong guess is correctable rather than permanent.
  const reject = async (row) => {
    const label = row.invoice_number || row.contact_name || "this bill";
    if (
      !window.confirm(
        `Put ${label} back in the list to record?\n\n` +
          "Use this when the MyGlobe record we matched it to is a different invoice."
      )
    )
      return;

    try {
      const res = await unmatchXeroBill(row.xero_invoice_id);
      if (res.success) {
        toast.success(res.message || "Match removed");
        load();
      } else {
        toast.error(res.message || "Could not remove the match");
      }
    } catch (e) {
      toast.error(e.message || "Could not remove the match");
    }
  };

  const changeRecorded = (next) => {
    setRecorded(next);
    setPage(1);
  };

  const lastSync = summary?.sync?.last_successful_at;
  const syncFailed = summary?.sync?.last_status === "failed";

  return (
    <div className="container-fluid mt-4">
      <h4 className="mb-3">Xero bills</h4>

      {/* One screen, two rhythms: a daily to-do list, and a periodic health check.
          Previously two menu entries, which showed the same "not yet recorded" count twice
          — and differently, since the queue defaults to contractor work. */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "queue" ? "active" : ""}`}
            onClick={() => setTab("queue")}
          >
            To record
            {summary?.bills?.unmatched > 0 && (
              <span className="badge bg-secondary ms-2">
                {summary.bills.unmatched.toLocaleString("en-GB")}
              </span>
            )}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "comparison" ? "active" : ""}`}
            onClick={() => setTab("comparison")}
          >
            Comparison
          </button>
        </li>
      </ul>

      {tab === "comparison" && <XeroReconciliation />}

      {tab === "queue" && (
        <>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="text-muted mb-0">
            Every supplier invoice in Xero. The list below is filtered by whether we can find
            it in MyGlobe — no bill is ever hidden.
          </p>
        </div>
        <button className="btn btn-outline-secondary" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {/* Sync state. A stale or failed sync must be visible — otherwise an empty queue
          looks like "nothing to do" when it actually means "no data arrived". */}
      {summary && (
        <div className={`alert ${syncFailed ? "alert-warning" : "alert-light"} py-2`}>
          <div className="row">
            <div className="col-md-3">
              <strong>{summary.bills.unmatched}</strong> awaiting entry
            </div>
            <div className="col-md-3">
              <strong>{summary.bills.unmatched_last_30_days}</strong> in the last 30 days
            </div>
            <div className="col-md-3">
              <strong>{summary.bills.matched}</strong> already recorded
            </div>
            <div className="col-md-3 text-md-end">
              {syncFailed ? (
                <span className="text-danger">
                  Last sync failed — figures may be out of date
                </span>
              ) : (
                <span className="text-muted">Last updated {fmtDate(lastSync)}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scope. Contractor work is what the purchase-to-payment process covers, so it is the
          default. Both counts are shown so nobody has to switch to find out what is on the
          other side. Nothing is excluded from the sync by this — only from this view. */}
      <div className="btn-group mb-3" role="group">
        <button
          type="button"
          className={`btn btn-sm ${scope === "contractors" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setScope("contractors");
            setPage(1);
          }}
        >
          Contractor work ({scopeCounts.contractors})
        </button>
        <button
          type="button"
          className={`btn btn-sm ${scope === "all" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setScope("all");
            setPage(1);
          }}
        >
          All suppliers ({scopeCounts.all})
        </button>
      </div>

      {/* The matching filter. Deliberately three buttons rather than a checkbox that hides
          things: every bill is reachable from here, and "All" proves it — the two figures
          above it add up to the third. A bill nobody can see is a bill nobody records. */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="text-muted small">Show:</span>
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${recorded === "0" ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => changeRecorded("0")}
          >
            To record ({recordedCounts.not_recorded})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${recorded === "1" ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => changeRecorded("1")}
            title="Bills we found a MyGlobe record for. Check these if something looks wrong."
          >
            Already recorded ({recordedCounts.recorded})
          </button>
          <button
            type="button"
            className={`btn btn-sm ${recorded === "all" ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => changeRecorded("all")}
            title="Every bill in Xero, matched or not."
          >
            All ({recordedCounts.all})
          </button>
        </div>
      </div>

      {recorded !== "0" && (
        <div className="alert alert-light py-2 small">
          Matching finds the MyGlobe record by invoice number and supplier. It can get one
          wrong. If a bill here is against the wrong record, use <strong>Not a match</strong>{" "}
          to put it back in the list to record.
        </div>
      )}

      <form className="row g-2 mb-3" onSubmit={submitSearch}>
        <div className="col-md-4">
          <input
            className="form-control"
            placeholder="Search invoice number or supplier"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" type="submit">
            Search
          </button>
          {search && (
            <button
              className="btn btn-link"
              type="button"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </div>
      </form>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th role="button" onClick={() => toggleSort("supplier")}>
                Supplier{sortIndicator("supplier")}
              </th>
              <th>Invoice no.</th>
              <th role="button" onClick={() => toggleSort("date")}>
                Date{sortIndicator("date")}
              </th>
              <th>Due</th>
              <th className="text-end" role="button" onClick={() => toggleSort("amount")}>
                Amount{sortIndicator("amount")}
              </th>
              <th>Status</th>
              {/* Only shown when matched bills can appear, so the daily work list stays
                  as narrow as it was. */}
              {recorded !== "0" && <th>In MyGlobe</th>}
              <th>Invoice</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={recorded === "0" ? 8 : 9} className="text-center text-muted py-4">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={recorded === "0" ? 8 : 9} className="text-center text-muted py-4">
                  {search ? (
                    "No bills match that search."
                  ) : recorded === "1" ? (
                    "No bills have been matched to a MyGlobe record yet."
                  ) : scope === "contractors" && scopeCounts.all > 0 ? (
                    <>
                      No contractor bills awaiting entry.{" "}
                      <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        onClick={() => setScope("all")}
                      >
                        Show all {scopeCounts.all} suppliers
                      </button>
                    </>
                  ) : (
                    "Nothing awaiting entry — every bill in Xero has been recorded."
                  )}
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((r) => (
                <tr key={r.xero_invoice_id}>
                  <td>
                    {/* Linked when the supplier resolves to a contractor record. A name
                        with no link means the supplier is not in the register — which is
                        itself worth seeing, since it is the root of the duplicate-name
                        problem in expenditure. */}
                    {r.contractor_id ? (
                      <a
                        href={`/contractors/${r.contractor_id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/contractors/${r.contractor_id}`);
                        }}
                        title={
                          r.contractor_name && r.contractor_name !== r.contact_name
                            ? `In MyGlobe: ${r.contractor_name}`
                            : "View contractor"
                        }
                      >
                        {r.contact_name}
                      </a>
                    ) : (
                      <span title="Not in the contractor list">{r.contact_name || "—"}</span>
                    )}
                  </td>
                  <td>{r.invoice_number || "—"}</td>
                  <td>{fmtDate(r.bill_date)}</td>
                  <td>{fmtDate(r.due_date)}</td>
                  <td className="text-end">{fmtMoney(r.total, r.currency)}</td>
                  <td>
                    <span className="badge bg-light text-dark">{r.status || "—"}</span>
                    {Number(r.amount_paid) > 0 && (
                      <span className="badge bg-success ms-1">Paid</span>
                    )}
                  </td>

                  {recorded !== "0" && (
                    <td>
                      {r.matched_expenditure_id ? (
                        <span
                          title={
                            r.match_method === "xero_link"
                              ? "Recorded from this screen — the MyGlobe record names this bill"
                              : "Matched on invoice number and supplier — a best guess"
                          }
                        >
                          #{r.matched_expenditure_id}
                          <span
                            className={`badge ms-2 ${
                              r.match_method === "xero_link"
                                ? "bg-success"
                                : "bg-warning text-dark"
                            }`}
                          >
                            {r.match_method === "xero_link" ? "Linked" : "Matched"}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted">Not recorded</span>
                      )}
                    </td>
                  )}

                  <td>
                    {/* Opens the bill in Xero, where the attached document is visible.
                        Not the Dext link held on the record — that needs a Dext login
                        which Finance staff generally do not have. */}
                    {r.xero_url ? (
                      <a
                        href={r.xero_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open this bill in Xero"
                      >
                        View in Xero
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    {!r.matched_expenditure_id ? (
                      // A draft in Xero can still change amount or be deleted. It stays on
                      // the list — hiding it is the failure this screen exists to prevent —
                      // but it cannot be recorded until Xero approves it.
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => record(r)}
                        disabled={r.recordable === false}
                        title={
                          r.recordable === false
                            ? `Not approved in Xero yet (${r.status}) — the amount can still change`
                            : undefined
                        }
                      >
                        Record
                      </button>
                    ) : r.match_method === "invoice_number" ? (
                      // Only a guess can be rejected. A definitive link means a real
                      // expenditure record names this bill, and removing that is the
                      // Expense Account screen's job.
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => reject(r)}
                        title="Put this bill back in the list to record"
                      >
                        Not a match
                      </button>
                    ) : (
                      <span className="text-muted small">Recorded</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-muted">
            Page {pagination.page} of {pagination.pages} — {pagination.total} bill
            {pagination.total === 1 ? "" : "s"}
          </span>
          <div>
            <button
              className="btn btn-outline-secondary btn-sm me-2"
              disabled={page <= 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= pagination.pages || loading}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default XeroBillsQueue;
